import { useState } from 'react';
import NextLink from 'next/link';

import { savedSearchApi } from 'api/savedSearches';

import { useUserContext } from 'contexts/UserContext';

import { Link } from 'components/Toolkit/Button/Link';

import { useAuthOptions } from 'hooks/useAuthOptions';

import { fireToast } from 'helpers/Toasts';
import { formatAuthorizationHeader } from 'helpers/auth';
import { pushToDataLayer } from 'services/gtmService';
import { alertTextFinder } from 'helpers/formatting';

import { USER_MENU } from 'components/Layouts/components/UserMenu';
import { NOTIFICATION_FREQUENCY } from 'components/SearchPage/hooks/useSavedSearch/useSavedSearch.typed';

import { formatSearchTerms } from 'helpers/formatSearchTerms';

type UseSaveSearchModalArgs = {
  adId: number;
  searchName?: string;
  searchTerms?: Array<string>;
};

const useSavedSearchModal = (args: UseSaveSearchModalArgs) => {
  const { adId, searchName, searchTerms } = args;
  const initialName = searchName ?? '';
  const terms = searchTerms;

  const [savedSearchName, setSavedSearchName] = useState(initialName);
  const [saving, setSaving] = useState(false);

  const { user } = useUserContext();
  const {
    setIsSearchSaved,
    selectedNotificationFrequency,
    setIsSavedSearchModalOpen,
  } = useUserContext();

  const { accessToken } = useAuthOptions();

  const authHeaders = formatAuthorizationHeader(accessToken);
  const userId = user?.id;

  const savedSearchPageHref = `/dashboard/${USER_MENU.SEARCHES}`;

  const handleOnSubmitSave = async () => {
    if (savedSearchName && terms) {
      const data = {
        name: savedSearchName,
        description: formatSearchTerms(terms),
        frequency:
          selectedNotificationFrequency ?? NOTIFICATION_FREQUENCY.DAILY,
      };
      try {
        setSaving(true);
        await savedSearchApi.createSavedSearchFromAd(
          data,
          userId?.toString() ?? '',
          adId,
          500,
          authHeaders,
        );
        setIsSearchSaved(true);
        setIsSavedSearchModalOpen(false);
        fireToast({
          type: 'SUCCESS',
          text: (
            <>
              Search Saved! Remember you can manage your preferences in&nbsp;
              <Link
                href={savedSearchPageHref}
                ofType="SECONDARY"
                inline
                NextLink={NextLink}
                underline
              >
                My DoneDeal
              </Link>
            </>
          ),
          closeOnClick: false,
        });
        pushToDataLayer({
          event: 'create_saved_search',
          alertType: [alertTextFinder(selectedNotificationFrequency)],
        });
      } catch (error) {
        fireToast({
          type: 'ERROR',
          text: 'There was an error saving your search, please try again',
        });
      }
      setSaving(false);
    }
  };

  return {
    savedSearchName,
    setSavedSearchName,
    handleOnSubmitSave,
    saving,
  };
};

export { useSavedSearchModal };
